/**
 * The north arrow of the day film (1.140.0). From use: "eine Anzeige der
 * Himmelsrichtung (rechte obere Ecke z.B.)", then decided: a round button
 * with a needle and "N", like Apple's maps, always there, turned with the
 * map and not tilted with it.
 *
 * Pure drawing and arithmetic without imports, so that `tourscript.ts` and
 * `compass.mjs` hold the very functions the film uses.
 */

/** The size of the disc and its margin from the top right corner, in units of the base film (1280 x 720). */
export const COMPASS_RADIUS = 26
export const COMPASS_MARGIN = 18

/**
 * Which way north points in the picture, radians clockwise from straight
 * up. Measured, not computed from the rotation: the camera's centre and a
 * point a little north of it go through the same `point()` that places the
 * pins and the vehicle, so the arrow agrees with the picture flat, tilted
 * and on the relief (`terrain.point`). A second formula for the rotation
 * would be a second place for the same rule.
 */
export function northOnScreen(point: (x: number, y: number) => [number, number], x: number, y: number, zoom: number): number {
  // Ten pixels of the map at this zoom; Mercator y grows to the south.
  const step = 10 / (256 * 2 ** zoom)
  const [ax, ay] = point(x, y)
  const [bx, by] = point(x, y - step)
  const dx = bx - ax
  const dy = by - ay
  if (Math.hypot(dx, dy) < 1e-9) return 0
  return Math.atan2(dx, -dy)
}

/**
 * The arrow in the top right corner of a picture `width` units wide, the
 * pen in units of the base film. The colours belong to the picture like
 * the vehicles' drawings (CLAUDE.md, `lib/vehicles.ts`), not to a theme:
 * the film looks the same in light and dark.
 */
export function drawCompass(
  pen: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  width: number,
  angle: number,
  letter: string,
): void {
  const cx = width - COMPASS_MARGIN - COMPASS_RADIUS
  const cy = COMPASS_MARGIN + COMPASS_RADIUS
  pen.save()
  pen.shadowColor = 'rgba(0,0,0,0.3)'
  pen.shadowBlur = 6
  pen.beginPath()
  pen.arc(cx, cy, COMPASS_RADIUS, 0, Math.PI * 2)
  pen.fillStyle = 'rgba(255,255,255,0.85)'
  pen.fill()
  pen.shadowColor = 'transparent'
  pen.translate(cx, cy)
  pen.rotate(angle)
  // The needle: north red, south grey, a diamond around the centre.
  const tip = COMPASS_RADIUS - 12
  pen.beginPath()
  pen.moveTo(0, -tip)
  pen.lineTo(6, 0)
  pen.lineTo(-6, 0)
  pen.closePath()
  pen.fillStyle = '#d0302a'
  pen.fill()
  pen.beginPath()
  pen.moveTo(0, tip)
  pen.lineTo(6, 0)
  pen.lineTo(-6, 0)
  pen.closePath()
  pen.fillStyle = '#8e939a'
  pen.fill()
  // The letter at the north end, turned with it, so that it says where north is.
  pen.font = 'bold 11px sans-serif'
  pen.textAlign = 'center'
  pen.textBaseline = 'middle'
  pen.fillStyle = '#d0302a'
  pen.fillText(letter, 0, -tip - 6)
  pen.restore()
}
